import { FastField, Field } from 'formik';
import KTFormGroup from 'general/components/OtherKeenComponents/Forms/KTFormGroup';
import KTFormInput, { KTFormInputType } from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import AppColors from 'general/constants/AppColors';
import { useTranslation } from 'react-i18next';
import { Row, Col, Modal } from 'react-bootstrap';
import { useMemo } from 'react';
import KTBSButton, { KTBSButtonColor, KTBSButtonSize, KTBSButtonType } from 'general/components/OtherKeenComponents/KTBSButton';
import UIHelper from 'general/helpers/UIHelper';
import DealerHelper from 'features/ManageDealer/dealerHelper';
import ChargeHelper from 'features/Charge/chargeHelper';
import CustomDropdown2 from 'general/components/Others/CustomDropdown2';

function AffiliateSaleCommissionView({
    formikProps,
    commissionRateCoinvestor,
    commissionModeList,
    commissionUnit,
    setCommissionUnit,
    isEnergyCommissionOn,
    handleToggle,
    selectedChargeStation,
    setSelectedChargeStation,
    readyToValidData,
}) {
    const { t } = useTranslation();

    const handleAddCommission = (values, setFieldValue) => {
        const list = values.coinvestorCommission ?? [];
        const nextId = list.length > 0 ? list[list.length - 1].id + 1 : 1;
        setFieldValue('coinvestorCommission', [...list, { id: nextId, revenue: 0, discount: 0 }]);
    };

    const handleDeleteCommission = (id, values, setFieldValue) => {
        const updated = values.coinvestorCommission.filter((item) => item.id !== id);
        setFieldValue(
            'coinvestorCommission',
            updated.length === 0 ? [{ id: 0, revenue: 0, discount: 0 }] : updated
        );
    };

    const handleSetCommissionUnit = (value) => {
        const unit = DealerHelper.checkPercentCommissionMode(value) ? '%' : 'VNĐ/kWh';
        setCommissionUnit(unit);
    };

    const dropdownChargeStationEl = useMemo(() => (
        <CustomDropdown2
            id="coinvestorStationId"
            name="coinvestorStationId"
            toggleElement={
                <button
                    className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between
                        ${!isEnergyCommissionOn ? 'disabled' : ''}
                        ${readyToValidData && isEnergyCommissionOn && !selectedChargeStation ? 'button-invalid' : ''}`}
                    type="button"
                >
                    {selectedChargeStation?.name ?? 'Tất cả'}
                </button>
            }
            selectedValue={selectedChargeStation}
            subTitleType="address"
            dropdownMenuItems={[{ name: 'Trạm sạc', role: 'STATION' }]}
            getSubItems={async (item) => {
                return ChargeHelper.API.requestGetChargeStationList({
                    page: item?.page || 0,
                    limit: 10,
                    q: item?.q || '',
                }).then((data) => ({
                    values: data.chargeStations?.map((station) => ({
                        id: station?.stationId,
                        name: station?.name,
                        item: station,
                        address: ChargeHelper.Utils.getChargeStationFullAddress(station),
                    })),
                    total: data.total,
                }));
            }}
            dropdownMenuStyle={{ maxHeight: '30vh' }}
            dropdownMenuClassName="overflow-auto w-100"
            onChange={(value) => setSelectedChargeStation(value)}
            isDropdownForm={true}
            titleSearchable={true}
            searchable={true}
            searchFormClassName="mx-3 my-1 search-form-border"
            ignoreConfirmClick={true}
            defaultValue={{ name: 'Trạm sạc', role: 'STATION' }}
            footerEl={
                <div
                    className="p-4 position-sticky sticky-bottom bg-white"
                    style={{ borderTop: `1px solid ${AppColors.gray[300]}` }}
                >
                    {UIHelper.buildNegativeButton('Bỏ chọn', () => setSelectedChargeStation(null))}
                </div>
            }
            disabled={!isEnergyCommissionOn}
        />
    ), [selectedChargeStation, readyToValidData, isEnergyCommissionOn]);

    const affiliateList = formikProps.values.coinvestorCommission ?? [];

    return (
        <Row>
            {/* Section 1 title */}
            <Col md={12}>
                <Modal.Title className="mb-3 pt-0">
                    <span className="font-weight-bolder" style={{ fontSize: 18, color: AppColors.gray[900] }}>
                        Hoa hồng bán liên kết
                    </span>
                </Modal.Title>
            </Col>

            {/* Base commission rate */}
            {commissionRateCoinvestor && (
                <Col md={4} className="pr-0">
                    <KTFormGroup
                        label={
                            <span className="font-weight-boldest" style={{ fontSize: 13, color: AppColors.gray[800] }}>
                                {t('Tỷ lệ hoa hồng')}
                            </span>
                        }
                        inputName="coinvestorCommission[0].discount"
                        additionalClassName=" mb-3"
                        inputElement={
                            <FastField name="coinvestorCommission[0].discount">
                                {({ field, form, meta }) => (
                                    <KTFormInput
                                        name={field.name}
                                        value={field.value}
                                        onChange={(value) => form.setFieldValue(field.name, value)}
                                        onBlur={() => form.setFieldTouched(field.name, true)}
                                        enableCheckValid
                                        type={KTFormInputType.number}
                                        placeholder="Nhập số"
                                        isValid={_.isEmpty(meta.error)}
                                        isTouched={meta.touched}
                                        feedbackText={meta.error}
                                        unit="%"
                                    />
                                )}
                            </FastField>
                        }
                    />
                </Col>
            )}

            {/* Tiered bonus rows */}
            {affiliateList.slice(1).map((item, index) => (
                <div key={item.id}>
                    <Row>
                        <Col md={7} className="pr-0" style={{ paddingLeft: 15 }}>
                            <KTFormGroup
                                label={
                                    <span className="font-weight-boldest" style={{ fontSize: 13, color: AppColors.gray[800] }}>
                                        {t('Doanh thu đạt mức')}
                                    </span>
                                }
                                inputName={`coinvestorCommission[${index + 1}].revenue`}
                                additionalClassName="mb-3"
                                inputElement={
                                    <FastField name={`coinvestorCommission[${index + 1}].revenue`}>
                                        {({ field, form, meta }) => (
                                            <KTFormInput
                                                name={field.name}
                                                value={field.value}
                                                onChange={(value) => form.setFieldValue(field.name, value)}
                                                onBlur={() => form.setFieldTouched(field.name, true)}
                                                enableCheckValid
                                                type={KTFormInputType.currency}
                                                placeholder="Nhập số"
                                                isValid={_.isEmpty(meta.error)}
                                                isTouched={meta.touched}
                                                feedbackText={meta.error}
                                            />
                                        )}
                                    </FastField>
                                }
                            />
                        </Col>
                        <Col md={5} className="pr-5">
                            <div className="d-flex gap-3 align-items-center">
                                <KTFormGroup
                                    label={
                                        <span className="font-weight-boldest" style={{ fontSize: 13, color: AppColors.gray[800] }}>
                                            {t('Thưởng thêm')}
                                        </span>
                                    }
                                    inputName={`coinvestorCommission[${index + 1}].discount`}
                                    additionalClassName=" mb-3 flex-grow-1"
                                    inputElement={
                                        <FastField name={`coinvestorCommission[${index + 1}].discount`}>
                                            {({ field, form, meta }) => (
                                                <KTFormInput
                                                    name={field.name}
                                                    value={field.value}
                                                    onChange={(value) => form.setFieldValue(field.name, value)}
                                                    onBlur={() => form.setFieldTouched(field.name, true)}
                                                    enableCheckValid
                                                    type={KTFormInputType.number}
                                                    placeholder="Nhập số"
                                                    isValid={_.isEmpty(meta.error)}
                                                    isTouched={meta.touched}
                                                    feedbackText={meta.error}
                                                    unit="%"
                                                />
                                            )}
                                        </FastField>
                                    }
                                />
                                <KTBSButton
                                    size={KTBSButtonSize.medium}
                                    color={KTBSButtonColor.secondary}
                                    type={KTBSButtonType.default}
                                    iconElement={<i className="fa-regular fa-trash-can" style={{ fontSize: 15 }} />}
                                    iconOnly
                                    borderRadius={5}
                                    onPress={() => handleDeleteCommission(item.id, formikProps.values, formikProps.setFieldValue)}
                                    additionalClassName="mt-4"
                                />
                            </div>
                        </Col>
                    </Row>
                </div>
            ))}

            {/* Add row button */}
            <Row>
                <Col md={1} style={{ paddingLeft: 15 }} className="d-flex align-items-center mb-4 mt-1">
                    <KTBSButton
                        title={t('add')}
                        size={KTBSButtonSize.medium}
                        color={KTBSButtonColor.secondary}
                        type={KTBSButtonType.default}
                        iconElement={<i className="fa-regular fa-plus" style={{ fontSize: 15 }} />}
                        additionalClassName="font-weight-bolder py-3 px-6"
                        borderRadius={5}
                        onPress={() => handleAddCommission(formikProps.values, formikProps.setFieldValue)}
                        buttonTitleColor={AppColors.gray[800]}
                        buttonTitleFontWeight={600}
                        buttonTitleFontSize={14}
                    />
                </Col>
            </Row>

            {/* Divider */}
            <Col md={12}>
                <div className="my-2" style={{ borderBottom: `1px dashed ${AppColors.gray[300]}` }} />
            </Col>

            {/* Section 2 title */}
            <Col md={12}>
                <Modal.Title className="mb-3 mt-5 d-flex gap-2 align-items-center">
                    <span className="font-weight-bolder" style={{ fontSize: 18, color: AppColors.gray[900] }}>
                        Hoa hồng tiền điện liên kết
                    </span>
                    {UIHelper.buildToggleSwitch(isEnergyCommissionOn, handleToggle)}
                </Modal.Title>
            </Col>

            {/* Commission mode dropdown */}
            <Col md={6} className="pr-0">
                <KTFormGroup
                    label={
                        <span className="font-weight-boldest" style={{ fontSize: 13, color: AppColors.gray[800] }}>
                            {t('commissionMode')}{' '}
                        </span>
                    }
                    inputName="coinvestorEnergyCommissionMode"
                    additionalClassName=" mb-0"
                    inputElement={
                        <Field name="coinvestorEnergyCommissionMode">
                            {({ field, form, meta }) => (
                                <KTBSDropdown
                                    toggleElement={
                                        <button
                                            className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between dropdown-disabled"
                                            type="button"
                                        >
                                            {field.value?.length
                                                ? t(`energyCommissionMode_${field.value}`)
                                                : t(`energyCommissionMode_${commissionModeList?.[0]}`)}
                                        </button>
                                    }
                                    selectedValue={field.value}
                                    dropdownMenuItems={commissionModeList?.map((mode) => ({
                                        name: t(`energyCommissionMode_${mode}`),
                                        value: mode,
                                    }))}
                                    dropdownMenuClassName="overflow-auto w-100"
                                    dropdownMenuStyle={{ maxHeight: '30vh' }}
                                    onChange={(value) => {
                                        form.setFieldValue(field.name, value);
                                        handleSetCommissionUnit(value);
                                    }}
                                    onBlur={() => form.setFieldTouched(field.name, true)}
                                    enableCheckValid
                                    isValid={_.isEmpty(meta.error)}
                                    isTouched={meta.touched}
                                    feedbackText={meta.error}
                                    isDropdownForm={true}
                                    disabled={true}
                                />
                            )}
                        </Field>
                    }
                    isRequired={isEnergyCommissionOn}
                />
            </Col>

            {/* Commission value */}
            <Col md={2} className="pr-0">
                <KTFormGroup
                    label={
                        <span className="font-weight-boldest" style={{ fontSize: 13, color: AppColors.gray[800] }}>
                            {t('commissionAmount')}{' '}
                        </span>
                    }
                    inputName="coinvestorEnergyCommissionValue"
                    additionalClassName=" mb-0"
                    inputElement={
                        <Field name="coinvestorEnergyCommissionValue">
                            {({ field, form, meta }) => (
                                <KTFormInput
                                    name={field.name}
                                    value={field.value}
                                    onChange={(value) => form.setFieldValue(field.name, value)}
                                    onBlur={() => form.setFieldTouched(field.name, true)}
                                    enableCheckValid
                                    type={commissionUnit === '%' ? KTFormInputType.number : KTFormInputType.currency}
                                    unit={commissionUnit}
                                    placeholder="Nhập số"
                                    isValid={_.isEmpty(meta.error)}
                                    isTouched={meta.touched}
                                    feedbackText={meta.error}
                                    disabled={!isEnergyCommissionOn}
                                />
                            )}
                        </Field>
                    }
                    isRequired={isEnergyCommissionOn}
                />
            </Col>

            {/* Commission duration */}
            <Col md={2} className="pr-0">
                <KTFormGroup
                    label={
                        <span className="font-weight-boldest" style={{ fontSize: 13, color: AppColors.gray[800] }}>
                            {t('commissionDuration')}{' '}
                        </span>
                    }
                    inputName="coinvestorEnergyCommissionDuration"
                    additionalClassName=" mb-0"
                    inputElement={
                        <Field name="coinvestorEnergyCommissionDuration">
                            {({ field, form, meta }) => (
                                <KTFormInput
                                    name={field.name}
                                    value={field.value}
                                    onChange={(value) => form.setFieldValue(field.name, value)}
                                    onBlur={() => form.setFieldTouched(field.name, true)}
                                    enableCheckValid
                                    type={KTFormInputType.number}
                                    unit="Tháng"
                                    placeholder="Nhập số"
                                    isValid={_.isEmpty(meta.error)}
                                    isTouched={meta.touched}
                                    feedbackText={meta.error}
                                    disabled={!isEnergyCommissionOn}
                                />
                            )}
                        </Field>
                    }
                    isRequired={isEnergyCommissionOn}
                />
            </Col>

            {/* Charge station dropdown */}
            <Col md={2}>
                {UIHelper.buildFormLabel('Trạm sạc áp dụng', isEnergyCommissionOn, true, 'mb-2')}
                {dropdownChargeStationEl}
            </Col>
        </Row>
    );
}

export default AffiliateSaleCommissionView;
